// services/voucherService.js
//
// Client-side API wrapper for voucher-related operations.
//
// WHY THIS FILE EXISTS
// -----------------------------------------------------------------------------
// - Keep all voucher network calls in ONE place.
// - Make server contracts explicit (query params, routes, response types).
// - Provide small, predictable helpers (query param normalization, serializer,
//   idempotency key generation).
//
// IMPORTANT: CURRENT APP CONTRACT (VoucherOverviewPage.js)
// -----------------------------------------------------------------------------
// The Voucher overview screen has TWO data-fetch modes:
//
// 1) NORMAL MODE
//    - Fetch list using filters (multi-status, date ranges, issuer list, etc.).
//    - Filters are sent as query params to: GET /api/vouchers
//
// 2) QR MODE (priority)
//    - If qrPayload is present, frontend sends ONLY { qrPayload }.
//    - Backend must do exact lookup by Voucher.qr_payload.
//    - All other filters are ignored in this mode.
//
// We DO NOT use the old archived=true/false behavior anymore.
// Status is the single source of truth: Draft / Active / Cancelled / Archived.
//
// -----------------------------------------------------------------------------
// Axios notes:
// - axios v1 supports AbortController signals via { signal }.
// - We still pass withCredentials for session cookies.
//

import apiClient from "../utils/apiClient";
import { isRequestCanceled } from "./httpUtils";

/* ============================================================================
   Small utilities
   ========================================================================== */

/**
 * Generate a simple Idempotency-Key (best-effort client-side).
 * Useful for "generate voucher number" flows where retries must be safe.
 *
 * Note:
 * - This is not cryptographically strong (doesn't need to be).
 * - Server should still enforce uniqueness/idempotency.
 */
export const genIdempotencyKey = () =>
  `idem_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

/**
 * Normalize a value to a trimmed string.
 * - Returns "" for non-string values.
 */
function normStr(v) {
  return typeof v === "string" ? v.trim() : "";
}

/** True if a value is a non-empty string after trimming. */
function hasText(v) {
  return normStr(v).length > 0;
}

/**
 * Convert "array-like" values into a clean string array.
 *
 * Supported inputs:
 * - array: [1, " 2 ", "", null] -> ["1", "2"]
 * - CSV string: "1, 2, 3" -> ["1", "2", "3"]
 * - single string: "7" -> ["7"]
 *
 * Output:
 * - trimmed
 * - no empty items
 * - preserves order
 * - de-duplicates (optional but helpful for userIds/status)
 */
function normalizeToArray(value, { dedupe = true } = {}) {
  let out = [];

  if (Array.isArray(value)) {
    out = value.map((x) => String(x).trim()).filter(Boolean);
  } else if (typeof value === "string") {
    const s = value.trim();
    if (!s) out = [];
    else if (s.includes(",")) out = s.split(",").map((x) => x.trim()).filter(Boolean);
    else out = [s];
  } else {
    out = [];
  }

  if (!dedupe) return out;

  const seen = new Set();
  return out.filter((x) => {
    if (seen.has(x)) return false;
    seen.add(x);
    return true;
  });
}

/**
 * Query params serializer
 * ---------------------------------------------------------------------------
 * We intentionally serialize arrays as repeated keys:
 *   status=Active&status=Draft
 *   userIds=1&userIds=2
 *
 * Why:
 * - It's explicit.
 * - It plays well with many backends (including Express + Sequelize parsing).
 * - It avoids ambiguity of CSV and avoids `[]` bracket conventions.
 *
 * Extra behavior:
 * - Omits undefined/null/empty-string values.
 * - Stable order (sort keys) -> easier debugging, consistent URLs.
 */
const paramsSerializer = (params) => {
  const usp = new URLSearchParams();

  const p = params || {};
  const keys = Object.keys(p).sort();

  for (const key of keys) {
    const val = p[key];

    if (val === undefined || val === null) continue;

    // Skip empty strings (important to avoid sending "createdAtFrom=" etc.)
    if (typeof val === "string" && val.trim() === "") continue;

    if (Array.isArray(val)) {
      for (const item of val) {
        if (item === undefined || item === null) continue;
        const s = String(item).trim();
        if (!s) continue;
        usp.append(key, s);
      }
      continue;
    }

    // booleans / numbers / anything else
    usp.append(key, String(val));
  }

  return usp.toString();
};

/* ============================================================================
   Voucher list fetch (VoucherOverviewPage)
   ========================================================================== */

/**
 * Build query params for GET /api/vouchers to match CURRENT app behavior.
 *
 * Accepted filters (VoucherOverviewPage.js):
 * - voucherNo, voucherSearchNo
 * - createdAtFrom, createdAtTo               (Voucher.createdAt day-range inclusive)
 * - expirationDateFrom, expirationDateTo     (Voucher.expiration_date day-range inclusive)
 * - redeemCreatedAtFrom, redeemCreatedAtTo   (RedeemVoucher.createdAt day-range inclusive, EXISTS)
 * - userIds                                 (issuer multi-select)
 * - type                                    ("Usługa" | "Kwota")
 * - status                                  (array: Active/Archived/Draft/Cancelled)
 * - qrPayload                                (QR MODE)
 *
 * IMPORTANT: QR MODE SHORT-CIRCUIT
 * - If qrPayload is present:
 *   -> return { qrPayload } only (ignore other filters)
 * - This keeps contract simple and backend logic deterministic.
 */
export function buildVoucherQueryParams(filters = {}) {
  const {
    voucherNo,
    voucherSearchNo,

    createdAtFrom,
    createdAtTo,

    expirationDateFrom,
    expirationDateTo,

    redeemCreatedAtFrom,
    redeemCreatedAtTo,

    userIds,
    type,

    status,
    qrPayload,
  } = filters;

  // ---------------------------------------------------------------------------
  // 1) QR MODE
  // ---------------------------------------------------------------------------
  const qr = normStr(qrPayload);
  if (qr) {
    return { qrPayload: qr };
  }

  // ---------------------------------------------------------------------------
  // 2) NORMAL MODE
  // ---------------------------------------------------------------------------
  const params = {};

  // Text search (backend uses contains by default: LIKE %term%)
  if (hasText(voucherNo)) params.voucherNo = normStr(voucherNo);
  if (hasText(voucherSearchNo)) params.voucherSearchNo = normStr(voucherSearchNo);

  // Type dropdown
  if (hasText(type)) params.type = normStr(type);

  // Date ranges (native date input value is YYYY-MM-DD)
  if (hasText(createdAtFrom)) params.createdAtFrom = normStr(createdAtFrom);
  if (hasText(createdAtTo)) params.createdAtTo = normStr(createdAtTo);

  if (hasText(expirationDateFrom)) params.expirationDateFrom = normStr(expirationDateFrom);
  if (hasText(expirationDateTo)) params.expirationDateTo = normStr(expirationDateTo);

  // Redeem EXISTS date range
  if (hasText(redeemCreatedAtFrom)) params.redeemCreatedAtFrom = normStr(redeemCreatedAtFrom);
  if (hasText(redeemCreatedAtTo)) params.redeemCreatedAtTo = normStr(redeemCreatedAtTo);

  // Issuer(s) - multi select
  const userIdArr = normalizeToArray(userIds);
  if (userIdArr.length) params.userIds = userIdArr;

  // Status(es) - multi select (toolbar checkboxes)
  const statusArr = normalizeToArray(status);
  if (statusArr.length) params.status = statusArr;

  return params;
}

/**
 * Fetch vouchers list from the server.
 *
 * Endpoint:
 *   GET /api/vouchers
 *
 * Typical usage in VoucherOverviewPage.js:
 *   fetchVouchers(effectiveFilters)                 // normal browsing
 *   fetchVouchers({ qrPayload: "..." })             // QR lookup
 *
 * Abort support:
 *   const controller = new AbortController();
 *   fetchVouchers(filters, { signal: controller.signal });
 */
export async function fetchVouchers(filters = {}, options = {}) {
  const params = buildVoucherQueryParams(filters);

  try {
    const res = await apiClient.get("/vouchers", {
      params,
      paramsSerializer,
      withCredentials: true,
      signal: options.signal,
    });
    return res.data;
  } catch (error) {
		
    // ✅ Cancellation is normal control flow (filters changing / StrictMode / navigation)
    if (isRequestCanceled(error)) {
      // Nie logujemy jako błąd.
      // Rzucamy dalej, żeby caller mógł przerwać swój flow bez ustawiania errorMsg.
      throw error;
    }
		
    // Keep error logs structured; helps in debugging production issues.
    console.error("[VoucherService] ❌ Error fetching vouchers", {
      message: error?.message,
      status: error?.response?.status,
      params,
    });
    throw error;
  }
}

/* ============================================================================
   Voucher lifecycle actions
   ========================================================================== */

/**
 * Request + lock a new voucher number (server creates a Draft placeholder).
 *
 * Endpoint:
 *   POST /api/vouchers/generate/:category
 *
 * Headers:
 *   Idempotency-Key: <optional>
 *
 * Notes:
 * - Server should treat same Idempotency-Key as "same result" (idempotent).
 */
export const fetchNextVoucherNumber = async (category, { idempotencyKey } = {}) => {
  try {
    const headers = {};
    if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

    const res = await apiClient.post(
      `/vouchers/generate/${encodeURIComponent(category)}`,
      {},
      { withCredentials: true, headers }
    );

    return res.data;
  } catch (error) {
    console.error("[VoucherService] ❌ Error fetching next voucher number:", error);
    throw error;
  }
};

/**
 * General voucher update (value, names, description, expiration_date, etc.).
 *
 * Endpoint:
 *   POST /api/vouchers/update/:id
 *
 * Body mirrors backend fields; backend contains business rules such as:
 * - Draft -> Active transition rules
 * - open derived from status (hook in model)
 */
export const updateVoucher = async (voucherId, voucherData) => {
  try {
    const res = await apiClient.post(`/vouchers/update/${voucherId}`, voucherData, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error("[VoucherService] ❌ Error updating voucher:", error);
    throw error;
  }
};

/**
 * Option A (atomic): Update voucher.to_note and (optionally) propagate to existing redeems.
 *
 * Endpoint:
 *   POST /api/vouchers/update/:id
 *
 * Body:
 *   { to_note: boolean, propagate_redeems?: boolean }
 *
 * Why:
 * - UI should not loop through redeems and patch them one-by-one.
 * - Backend can do this transactionally and efficiently.
 */
export const updateVoucherToNote = async (voucherId, toNote, { propagate = false } = {}) => {
  try {
    const res = await apiClient.post(
      `/vouchers/update/${voucherId}`,
      { to_note: !!toNote, propagate_redeems: !!propagate },
      { withCredentials: true }
    );
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error updating to_note for ${voucherId}:`, error);
    throw error;
  }
};

/**
 * Optional helper if you expose a dedicated propagation endpoint on backend.
 *
 * Endpoint:
 *   POST /api/vouchers/:voucherId/propagate-note
 * Body:
 *   { noted: boolean }
 *
 * Returns:
 *   { message, updated }
 */
export const propagateRedeemsNoted = async (voucherId, noted) => {
  try {
    const res = await apiClient.post(
      `/vouchers/${voucherId}/propagate-note`,
      { noted: !!noted },
      { withCredentials: true }
    );
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error propagating noted for voucher ${voucherId}:`, error);
    throw error;
  }
};

/**
 * Cancel a voucher (manual cancel).
 *
 * Endpoint:
 *   POST /api/vouchers/cancel/:id
 */
export const cancelVoucher = async (voucherId) => {
  try {
    await apiClient.post(`/vouchers/cancel/${voucherId}`, {}, { withCredentials: true });
  } catch (error) {
    console.error("[VoucherService] ❌ Error canceling voucher:", error);
    throw error;
  }
};

/**
 * Archive a voucher.
 *
 * Endpoint:
 *   PATCH /api/vouchers/archive/:id
 */
export const archiveVoucher = async (voucherId) => {
  try {
    await apiClient.patch(`/vouchers/archive/${voucherId}`, {}, { withCredentials: true });
  } catch (error) {
    console.error(`[VoucherService] ❌ Error archiving voucher ${voucherId}:`, error);
    throw error;
  }
};

/**
 * Restore an archived voucher back to Active status.
 *
 * Endpoint:
 *   PATCH /api/vouchers/unarchive/:id
 *
 * Backend intentionally restricts this action to superusers because it is a
 * corrective administrative operation. The frontend still keeps this wrapper
 * generic and lets the backend remain the final authority.
 */
export const unarchiveVoucher = async (voucherId) => {
  try {
    await apiClient.patch(`/vouchers/unarchive/${voucherId}`, {}, {
      withCredentials: true,
    });
  } catch (error) {
    console.error(`[VoucherService] ❌ Error unarchiving voucher ${voucherId}:`, error);
    throw error;
  }
};


/**
 * Read suggested e-mail recipient values for the Voucher Overview send dialog.
 *
 * Endpoint:
 *   GET /api/vouchers/:id/email-defaults
 *
 * Why this is a separate read call:
 * - normal voucher list rows do not need to carry full order/e-mail metadata,
 * - online voucher defaults are needed only when the user explicitly opens the
 *   send dialog,
 * - backend remains the source of truth for buyer/recipient order linkage.
 */
export const getVoucherEmailDefaults = async (voucherId) => {
  try {
    const res = await apiClient.get(`/vouchers/${voucherId}/email-defaults`, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error reading email defaults for voucher ${voucherId}:`, error);
    throw error;
  }
};

/**
 * Send a voucher/package by e-mail from the internal Voucher Overview.
 *
 * Endpoint:
 *   POST /api/vouchers/:id/send-email
 *
 * Payload:
 *   {
 *     to: string,
 *     cc?: string,
 *     attachPdf: boolean
 *   }
 *
 * Important:
 * - every authenticated internal role may call this endpoint according to the
 *   current business decision; the backend still validates the session and
 *   remains the final authority,
 * - sending an e-mail does not change voucher status or value.
 */
export const sendVoucherEmail = async (voucherId, payload) => {
  try {
    const res = await apiClient.post(`/vouchers/${voucherId}/send-email`, payload, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error sending voucher ${voucherId} by e-mail:`, error);
    throw error;
  }
};

/* ============================================================================
   PDF
   ========================================================================== */

/**
 * Download server-generated PDF for a voucher.
 *
 * Endpoint:
 *   GET /api/vouchers/:id/pdf
 *
 * Returns:
 * - Blob (application/pdf)
 */
export const downloadVoucherPdf = async (voucherId) => {
  try {
    const res = await apiClient.get(`/vouchers/${voucherId}/pdf`, {
      responseType: "blob",
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error downloading voucher PDF ${voucherId}:`, error);
    throw error;
  }
};

/* ============================================================================
   Redeem: create / history / get / update
   ========================================================================== */

/**
 * Redeem a voucher.
 *
 * Endpoint:
 *   POST /api/vouchers/redeem/:voucherId
 *
 * Body:
 *   {
 *     reservation_id?: string | null,
 *     redeemed_amount: number|string,
 *     noted?: boolean,
 *     comment?: string
 *   }
 *
 * Notes:
 * - If `noted` is omitted, backend should default it to voucher.to_note.
 * - reservation_id is nullable in DB (redeem_vouchers.reservation_id allowNull: true).
 */
export const redeemVoucher = async (
  voucherId,
  reservationId,
  redeemedAmount,
  noted = undefined,
  comment = undefined
) => {
  try {
    const body = {
      reservation_id: reservationId ?? null,
      redeemed_amount: redeemedAmount,
    };

    // optional overrides
    if (typeof noted === "boolean") body.noted = noted;
    if (typeof comment === "string") body.comment = comment;

    const res = await apiClient.post(`/vouchers/redeem/${voucherId}`, body, {
      withCredentials: true,
    });

    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error redeeming voucher ${voucherId}:`, error);
    throw error;
  }
};

/**
 * Fetch redeem history for a voucher.
 *
 * Endpoint:
 *   GET /api/vouchers/redeem-history/:voucherId
 *
 * Note:
 * - Some backends return 204/404 when empty; UI may treat that as "no rows".
 */
export const fetchRedeemHistory = async (voucherId) => {
  try {
    const res = await apiClient.get(`/vouchers/redeem-history/${voucherId}`, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error fetching redeem history ${voucherId}:`, error);
    throw error;
  }
};

/**
 * Get a single redeem entry by id.
 *
 * Endpoint:
 *   GET /api/vouchers/redeems/:redeemId
 */
export const getRedeemEntry = async (redeemId) => {
  try {
    const res = await apiClient.get(`/vouchers/redeems/${redeemId}`, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error getting redeem entry ${redeemId}:`, error);
    throw error;
  }
};

/**
 * Get a single voucher by id.
 *
 * Endpoint:
 *   GET /api/vouchers/:id
 *
 * Returns:
 * - minimal details (usually includes issuer)
 */
export const getVoucherById = async (voucherId) => {
  try {
    const res = await apiClient.get(`/vouchers/${voucherId}`, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error getting voucher ${voucherId}:`, error);
    throw error;
  }
};

/**
 * Update a redeem entry — legacy route kept for backward compatibility.
 *
 * Endpoint:
 *   POST /api/vouchers/:voucherId/redeems/update/:redeemId
 *
 * Recommendation:
 * - Prefer updateRedeemEntryPatch() below if the backend supports it.
 */
export const updateRedeemEntry = async (voucherId, redeemId, payload) => {
  try {
    const res = await apiClient.post(
      `/vouchers/${voucherId}/redeems/update/${redeemId}`,
      payload,
      { withCredentials: true }
    );
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error while updating redeem entry ${redeemId}:`, error);
    throw error;
  }
};

/**
 * Update a redeem entry — preferred route (REST).
 *
 * Endpoint:
 *   PATCH /api/vouchers/:voucherId/redeems/:redeemId
 */
export const updateRedeemEntryPatch = async (voucherId, redeemId, payload) => {
  try {
    const res = await apiClient.patch(`/vouchers/${voucherId}/redeems/${redeemId}`, payload, {
      withCredentials: true,
    });
    return res.data;
  } catch (error) {
    console.error(`[VoucherService] ❌ Error (PATCH) updating redeem entry ${redeemId}:`, error);
    throw error;
  }
};
